import { configureStore, combineReducers, createAction } from '@reduxjs/toolkit';
import storage from 'redux-persist/lib/storage';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';

/* reducers */
import authReducer from './auth/auth-slice';
import nutritionReducer from './nutrition/nutrition-slice';
import programReducer from './programs/programs-slice';
import surveyReducer from './survey/survey-slice';
import risksReducer from './risks/risks-slice';
import analysisReducer from './analysis/analysis-slice';
import statisticsReducer from './statistics/statistics-slice';
import { StatisticsState } from './statistics/StatisticsTypes';
import trainingsReducer from './trainings/trainings-slice';
import policiesReducer from './policies/policies-slice';
import mentalHealthReducer from './mentalHealth/mentalHealth-slice';
// import vitaminsReducer from './vitamins/vitamins-slice';
import metricReducer from './metric/metric-slice';

/* types */
import { AuthState } from './auth/AuthTypes';
import { NutritionState } from './nutrition/NutritionTypes';
import { ProgramsState } from './programs/ProgramsTypes';
import { SurveyState } from './survey/SurveyTypes';
import { RisksState } from './risks/RisksTypes';
import { AnalysisState } from './analysis/AnalysisTypes';
// import { StatisticsState } from './statistics/StatisticsTypes';
import { TrainingsState } from './trainings/TrainingsTypes';
import { PoliciesState } from './policies/PoliciesTypes';
import vitaminsReducer from './vitamins/vitamins-slice';
// import { VitaminsState } from './vitamins/VitaminsTypes';
import { MetricState } from './metric/MetricTypes';
import { MentalHealthState } from './mentalHealth/MentalHealthTypes';
import { VitaminsState } from '@store/vitamins/VitaminsTypes';
import surveyInviteReducer from './surveyInvite/surveyInvite-slice';
import adminAuthReducer from './adminAuth/adminAuth-slice';
import impersonationReducer from './impersonation/impersonation-slice';
import adminPanelReducer from './adminPanel/adminPanel-slice';

export interface RootState {
  auth: AuthState;
  nutrition: NutritionState;
  programs: ProgramsState;
  survey: SurveyState;
  risks: RisksState;
  statistics: StatisticsState;
  analysis: AnalysisState;
  // statistics: StatisticsState;
  trainings: TrainingsState;
  policies: PoliciesState;
  mentalHealth: MentalHealthState;
  // vitamins: VitaminsState;
  metric: MetricState;
  vitamins: VitaminsState;
  surveyInvite: ReturnType<typeof surveyInviteReducer>;
  adminAuth: ReturnType<typeof adminAuthReducer>;
  impersonation: ReturnType<typeof impersonationReducer>;
  adminPanel: ReturnType<typeof adminPanelReducer>;
}

const persistConfigs = {
  auth: {
    key: 'auth',
    storage,
    // Сохраняем только устойчивые auth-данные.
    // Технические флаги (loading/error/initialized) не должны переживать перезагрузку.
    whitelist: ['accessToken', 'tokenType', 'user', 'isLoggedIn'],
  },
  nutrition: {
    key: 'nutrition',
    storage,
  },
  programs: {
    key: 'programs',
    storage,
  },
  survey: {
    key: 'survey',
    storage,
  },
  risks: {
    key: 'risks',
    storage,
  },
  analysis: {
    key: 'analysis',
    storage,
  },
  statistics: {
    key: 'statistics',
    storage,
  },
  trainings: {
    key: 'trainings',
    storage,
  },
  policies: {
    key: 'policies',
    storage,
  },
  vitamins: {
    key: 'vitamins',
    storage,
  },
  metric: {
    key: 'metric',
    storage,
    whitelist: ['location', 'guestId', 'firstTimePages'],
  },
  mentalHealth: {
    key: 'mentalHealth',
    storage,
    // Only the in-progress questionnaire draft — not API results/history.
    whitelist: ['answers', 'showForm', 'currentQuestionIndex'],
  },
};

const appReducer = combineReducers({
  auth: persistReducer(persistConfigs.auth, authReducer),
  nutrition: persistReducer(persistConfigs.nutrition, nutritionReducer),
  programs: persistReducer(persistConfigs.programs, programReducer),
  survey: persistReducer(persistConfigs.survey, surveyReducer),
  risks: persistReducer(persistConfigs.risks, risksReducer),
  analysis: persistReducer(persistConfigs.analysis, analysisReducer),
  statistics: persistReducer(persistConfigs.statistics, statisticsReducer),
  trainings: persistReducer(persistConfigs.trainings, trainingsReducer),
  policies: persistReducer(persistConfigs.policies, policiesReducer),
  mentalHealth: persistReducer(persistConfigs.mentalHealth, mentalHealthReducer),
  // vitamins: persistReducer(persistConfigs.vitamins, vitaminsReducer),
  metric: persistReducer(persistConfigs.metric, metricReducer),
  vitamins: persistReducer(persistConfigs.vitamins, vitaminsReducer),
  surveyInvite: surveyInviteReducer,
  adminAuth: adminAuthReducer,
  impersonation: impersonationReducer,
  adminPanel: adminPanelReducer,
});

// Action creator для сброса всего store
export const resetStore = createAction('RESET_STORE');

const rootReducer = (state: any, action: any) => {
  if (resetStore.match(action)) {
    // Сбрасываем все состояние к undefined, что приведет к использованию initial state
    state = undefined;
  }
  return appReducer(state, action);
};

const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

const persistor = persistStore(store);

export { store, persistor };
export type AppDispatch = typeof store.dispatch;
export type AppState = ReturnType<typeof store.getState>;
